import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setActiveWorkspaceId, getActiveWorkspaceId } from "../lib/api";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveIdState] = useState(getActiveWorkspaceId());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data.workspaces || []);
      // Honor backend's active_workspace_id when we have no local pick OR our pick isn't a member anymore.
      const localValid = (data.workspaces || []).some((w) => w.id === activeId);
      const next = localValid ? activeId : data.active_workspace_id;
      if (next && next !== activeId) {
        setActiveWorkspaceId(next);
        setActiveIdState(next);
      } else if (next && !activeId) {
        setActiveWorkspaceId(next);
        setActiveIdState(next);
      }
    } catch {
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [user, activeId]);

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const switchWorkspace = (id) => {
    setActiveWorkspaceId(id);
    setActiveIdState(id);
    // Hard reload so every query refetches under the new workspace scope cleanly.
    window.location.reload();
  };

  const active = workspaces.find((w) => w.id === activeId) || null;

  return (
    <WorkspaceContext.Provider value={{ workspaces, active, activeId, switchWorkspace, refresh, loading }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext);
